/**
 * Packing Redux Slice
 * Manages all packing-related state in Redux
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var _a;
import { createSlice } from '@reduxjs/toolkit';
import { PackingSubscreen } from '../../components/packing_v2/types';
import { fetchShipmentDetails, fetchShipmentPackages, packItem, packKitItem, unpackItem, closePackage, deletePackage, resetPackage, resetAllPackages, deleteAllPackages, updatePackage, markShipmentPacked, markShipmentShipped, fetchBoxTypes, fetchWeightUoMs, fetchShippingMethods, fetchShipmentList, fetchAttachments, fetchCountries, fetchStates, updateAddress, getReturnLabel, fetchUserPreferences, fetchDefaultNetworkPrinterDetail, fetchPrinterList, setDefaultNetworkPrinterDetail, setUserPreference, closeBulkPackage, fetchShipmentItemAssets, fetchRefundLabel, fetchFacilities, fetchEnumeration, fetchStatusList, initializePacking } from './packingThunks';
import { containerScan, containerBulkCreate, containerCreate, containerClose, containerReopen, containerDelete, containerReset, containerResetAndReturn } from './containerThunks';
// Initial loading state
export var initialLoadingState = {
    shipment: false,
    packItem: false,
    closePackage: false,
    deletePackage: false,
    deleteAllPackages: false,
    resetPackage: false,
    resetAllPackages: false,
    updatePackage: false,
    markPacked: false,
    markShipped: false,
    getLabel: false,
    shipmentList: false,
    boxTypes: false,
    weightUoMs: false,
    shippingMethods: false,
    countries: false,
    states: false,
    updateAddress: false,
    returnLabel: false,
    attachments: false,
    userPreferences: false,
    containerScan: false,
    containerCreate: false,
    containerClose: false,
    containerDelete: false,
    containerReset: false,
    containerResetAndReturn: false
};
// Initial error state
export var initialErrorState = {
    shipment: null,
    packItem: null,
    closePackage: null,
    deletePackage: null,
    deleteAllPackages: null,
    resetPackage: null,
    resetAllPackages: null,
    updatePackage: null,
    markPacked: null,
    markShipped: null,
    getLabel: null,
    shipmentList: null,
    boxTypes: null,
    weightUoMs: null,
    shippingMethods: null,
    countries: null,
    states: null,
    updateAddress: null,
    returnLabel: null,
    attachments: null,
    userPreferences: null,
    containerScan: null,
    containerCreate: null,
    containerClose: null,
    containerDelete: null,
    containerReset: null,
    containerResetAndReturn: null
};
// Initial state
export var initialPackingState = {
    shipmentData: null,
    shipmentId: null,
    packageContentBySeqId: {},
    packageOrderItemsBySeqId: {},
    packageSerialNumbersBySeqId: {},
    containerPackerById: {},
    packageMetadataBySeqId: {},
    currentSubscreen: PackingSubscreen.ShipmentId,
    boxTypes: [],
    weightUoMs: [],
    shippingMethods: [],
    countriesList: [],
    statesList: [],
    userPreferences: {},
    printerList: [],
    qzHostname: null,
    isQzConnected: false,
    isQzHostnameChecked: false,
    shipmentList: [],
    shipmentListPageMaxIndex: 0,
    shipmentListRecordCount: 0,
    containerId: null,
    attachmentList: [],
    shipmentStatusList: [],
    shipmentTypeList: [],
    shipmentTypeMethodList: [],
    shipmentLocationTypeList: [],
    facilitiesData: [],
    itemAssets: [],
    shipmentContainerList: [],
    containerSerialNumbersByContainerId: {},
    containerTypeList: [],
    bulkPackMode: false,
    scanThroughPack: false,
    scanIdCache: {},
    scanQueuePendingCount: 0,
    scanQueueFlushing: false,
    loading: initialLoadingState,
    errors: initialErrorState
};
export var packingSlice = createSlice({
    name: 'packing',
    initialState: initialPackingState,
    reducers: {
        // Reset all packing state
        resetPackingState: function () { return initialPackingState; },
        // Set current subscreen
        setCurrentSubscreen: function (state, action) {
            state.currentSubscreen = action.payload;
        },
        // Set container ID for bin scanning
        setContainerId: function (state, action) {
            state.containerId = action.payload;
        },
        // Toggle bulk pack mode
        setBulkPackMode: function (state, action) {
            state.bulkPackMode = action.payload;
        },
        // Toggle scan through pack
        setScanThroughPack: function (state, action) {
            state.scanThroughPack = action.payload;
        },
        // cache a resolved barcode - known non-product has productId = null
        setScanIdCacheEntry: function (state, action) {
            var _a;
            state.scanIdCache[action.payload.cacheKey] = {
                productId: action.payload.productId,
                pseudoId: (_a = action.payload.pseudoId) !== null && _a !== void 0 ? _a : null
            };
        },
        // scan queue status
        setScanQueueStatus: function (state, action) {
            state.scanQueuePendingCount = action.payload.pendingCount;
            state.scanQueueFlushing = action.payload.flushing;
        },
        // Clear shipment data
        clearShipmentData: function (state) {
            state.shipmentData = null;
            state.shipmentId = null;
            state.packageContentBySeqId = {};
            state.packageOrderItemsBySeqId = {};
            state.packageSerialNumbersBySeqId = {};
            state.containerPackerById = {};
            state.packageMetadataBySeqId = {};
            state.attachmentList = [];
            state.itemAssets = [];
            state.shipmentContainerList = [];
            state.containerSerialNumbersByContainerId = {};
            // scanIdCache survives deliberately (org-scoped, not shipment-scoped)
            state.scanQueuePendingCount = 0;
            state.scanQueueFlushing = false;
        },
        // Clear errors
        clearErrors: function (state) {
            state.errors = initialErrorState;
        },
        // Clear specific error
        clearError: function (state, action) {
            state.errors[action.payload] = null;
        },
        // Set shipment status list
        setShipmentStatusList: function (state, action) {
            state.shipmentStatusList = action.payload;
        },
        // Set shipment type list
        setShipmentTypeList: function (state, action) {
            state.shipmentTypeList = action.payload;
        },
        // Set shipment type method list
        setShipmentTypeMethodList: function (state, action) {
            state.shipmentTypeMethodList = action.payload;
        },
        // Set shipment location type list
        setShipmentLocationTypeList: function (state, action) {
            state.shipmentLocationTypeList = action.payload;
        },
        // Set facilities data
        setFacilitiesData: function (state, action) {
            state.facilitiesData = action.payload;
        },
        setQzDeviceContext: function (state, action) {
            var _a, _b;
            state.isQzConnected = action.payload.connected;
            state.qzHostname = (_a = action.payload.hostname) !== null && _a !== void 0 ? _a : null;
            state.isQzHostnameChecked = (_b = action.payload.isChecked) !== null && _b !== void 0 ? _b : true;
        },
        setQzConnectionActive: function (state, action) {
            state.isQzConnected = action.payload;
            if (!action.payload) {
                state.qzHostname = null;
            }
        }
    },
    extraReducers: function (builder) {
        // Fetch shipment details
        builder
            .addCase(fetchShipmentDetails.pending, function (state, action) {
            var _a;
            var isSilent = ((_a = action.meta.arg) === null || _a === void 0 ? void 0 : _a.silent) === true;
            if (!isSilent) {
                // Keep existing blocking/loading UX for explicit user-driven fetches.
                state.loading.shipment = true;
                state.errors.shipment = null;
            }
        })
            .addCase(fetchShipmentDetails.fulfilled, function (state, action) {
            var _a, _b, _c, _d;
            var isSilent = ((_a = action.meta.arg) === null || _a === void 0 ? void 0 : _a.silent) === true;
            if (!isSilent) {
                // Silent refreshes skip the global loading spinner.
                state.loading.shipment = false;
            }
            if (action.payload) {
                state.shipmentData = action.payload.shipmentData;
                state.shipmentId = ((_b = action.payload.shipmentData) === null || _b === void 0 ? void 0 : _b.shipmentId) || null;
                state.packageMetadataBySeqId = action.payload.packageMetadataBySeqId;
                state.shipmentContainerList =
                    ((_c = action.payload.shipmentData) === null || _c === void 0 ? void 0 : _c.shipmentContainerList) || [];
                state.containerSerialNumbersByContainerId = (((_d = action.payload.shipmentData) === null || _d === void 0 ? void 0 : _d.shipmentContainerSerialNumberList) || []).reduce(function (acc, serialItem) {
                    var _a;
                    if (!(serialItem === null || serialItem === void 0 ? void 0 : serialItem.containerId) || !(serialItem === null || serialItem === void 0 ? void 0 : serialItem.serialNumber))
                        return acc;
                    acc[serialItem.containerId] = (_a = acc[serialItem.containerId]) !== null && _a !== void 0 ? _a : [];
                    acc[serialItem.containerId].push(serialItem);
                    return acc;
                }, {});
                state.currentSubscreen = PackingSubscreen.ScanProduct;
            }
        })
            .addCase(fetchShipmentDetails.rejected, function (state, action) {
            var _a;
            var isSilent = ((_a = action.meta.arg) === null || _a === void 0 ? void 0 : _a.silent) === true;
            if (!isSilent) {
                // Silent refresh failures should not surface as blocking shipment errors.
                state.loading.shipment = false;
                state.errors.shipment = action.payload || 'Failed to fetch shipment';
            }
        });
        // Fetch shipment packages
        builder.addCase(fetchShipmentPackages.fulfilled, function (state, action) {
            if (action.payload) {
                state.packageContentBySeqId = action.payload.packageContentBySeqId;
                state.packageOrderItemsBySeqId = action.payload.packageOrderItemsBySeqId;
                state.packageSerialNumbersBySeqId = action.payload.packageSerialNumbersBySeqId;
                state.containerPackerById = action.payload.containerPackerById;
            }
        });
        // Pack item
        builder
            .addCase(packItem.pending, function (state, action) {
            var _a;
            if ((_a = action.meta.arg) === null || _a === void 0 ? void 0 : _a.suppressRefresh)
                return;
            state.loading.packItem = true;
            state.errors.packItem = null;
        })
            .addCase(packItem.fulfilled, function (state, action) {
            var _a;
            if ((_a = action.meta.arg) === null || _a === void 0 ? void 0 : _a.suppressRefresh)
                return;
            state.loading.packItem = false;
        })
            .addCase(packItem.rejected, function (state, action) {
            var _a;
            if ((_a = action.meta.arg) === null || _a === void 0 ? void 0 : _a.suppressRefresh)
                return;
            state.loading.packItem = false;
            state.errors.packItem = action.payload || 'Failed to pack item';
        });
        // Pack kit item
        builder
            .addCase(packKitItem.pending, function (state) {
            state.loading.packItem = true;
            state.errors.packItem = null;
        })
            .addCase(packKitItem.fulfilled, function (state) {
            state.loading.packItem = false;
        })
            .addCase(packKitItem.rejected, function (state, action) {
            state.loading.packItem = false;
            state.errors.packItem = action.payload || 'Failed to pack kit item';
        });
        // Unpack item
        builder
            .addCase(unpackItem.pending, function (state) {
            state.loading.packItem = true;
            state.errors.packItem = null;
        })
            .addCase(unpackItem.fulfilled, function (state) {
            state.loading.packItem = false;
        })
            .addCase(unpackItem.rejected, function (state, action) {
            state.loading.packItem = false;
            state.errors.packItem = action.payload || 'Failed to unpack item';
        });
        // Close package
        builder
            .addCase(closePackage.pending, function (state) {
            state.loading.closePackage = true;
            state.errors.closePackage = null;
        })
            .addCase(closePackage.fulfilled, function (state) {
            state.loading.closePackage = false;
        })
            .addCase(closePackage.rejected, function (state, action) {
            state.loading.closePackage = false;
            state.errors.closePackage = action.payload || 'Failed to close package';
        });
        // Close bulk package
        builder
            .addCase(closeBulkPackage.pending, function (state) {
            state.loading.closePackage = true;
            state.errors.closePackage = null;
        })
            .addCase(closeBulkPackage.fulfilled, function (state) {
            state.loading.closePackage = false;
        })
            .addCase(closeBulkPackage.rejected, function (state, action) {
            state.loading.closePackage = false;
            state.errors.closePackage = action.payload || 'Failed to close bulk package';
        });
        // Delete package
        builder
            .addCase(deletePackage.pending, function (state) {
            state.loading.deletePackage = true;
            state.errors.deletePackage = null;
        })
            .addCase(deletePackage.fulfilled, function (state) {
            state.loading.deletePackage = false;
        })
            .addCase(deletePackage.rejected, function (state, action) {
            state.loading.deletePackage = false;
            state.errors.deletePackage = action.payload || 'Failed to delete package';
        });
        // Delete all packages
        builder
            .addCase(deleteAllPackages.pending, function (state) {
            state.loading.deleteAllPackages = true;
            state.errors.deleteAllPackages = null;
        })
            .addCase(deleteAllPackages.fulfilled, function (state) {
            state.loading.deleteAllPackages = false;
        })
            .addCase(deleteAllPackages.rejected, function (state, action) {
            state.loading.deleteAllPackages = false;
            state.errors.deleteAllPackages =
                action.payload || 'Failed to delete all packages';
        });
        // Reset package
        builder
            .addCase(resetPackage.pending, function (state) {
            state.loading.resetPackage = true;
            state.errors.resetPackage = null;
        })
            .addCase(resetPackage.fulfilled, function (state) {
            state.loading.resetPackage = false;
        })
            .addCase(resetPackage.rejected, function (state, action) {
            state.loading.resetPackage = false;
            state.errors.resetPackage = action.payload || 'Failed to reset package';
        });
        // Reset all packages
        builder
            .addCase(resetAllPackages.pending, function (state) {
            state.loading.resetAllPackages = true;
            state.errors.resetAllPackages = null;
        })
            .addCase(resetAllPackages.fulfilled, function (state) {
            state.loading.resetAllPackages = false;
        })
            .addCase(resetAllPackages.rejected, function (state, action) {
            state.loading.resetAllPackages = false;
            state.errors.resetAllPackages =
                action.payload || 'Failed to reset all packages';
        });
        // Update package
        builder
            .addCase(updatePackage.pending, function (state) {
            state.loading.updatePackage = true;
            state.errors.updatePackage = null;
        })
            .addCase(updatePackage.fulfilled, function (state) {
            state.loading.updatePackage = false;
        })
            .addCase(updatePackage.rejected, function (state, action) {
            state.loading.updatePackage = false;
            state.errors.updatePackage = action.payload || 'Failed to update package';
        });
        // Mark shipment packed
        builder
            .addCase(markShipmentPacked.pending, function (state) {
            state.loading.markPacked = true;
            state.errors.markPacked = null;
        })
            .addCase(markShipmentPacked.fulfilled, function (state) {
            state.loading.markPacked = false;
        })
            .addCase(markShipmentPacked.rejected, function (state, action) {
            state.loading.markPacked = false;
            state.errors.markPacked = action.payload || 'Failed to mark shipment as packed';
        });
        // Mark shipment shipped
        builder
            .addCase(markShipmentShipped.pending, function (state) {
            state.loading.markShipped = true;
            state.errors.markShipped = null;
        })
            .addCase(markShipmentShipped.fulfilled, function (state) {
            state.loading.markShipped = false;
            state.currentSubscreen = PackingSubscreen.ShipmentId;
        })
            .addCase(markShipmentShipped.rejected, function (state, action) {
            state.loading.markShipped = false;
            state.errors.markShipped =
                action.payload || 'Failed to mark shipment as shipped';
        });
        // Fetch box types
        builder
            .addCase(fetchBoxTypes.pending, function (state) {
            state.loading.boxTypes = true;
            state.errors.boxTypes = null;
        })
            .addCase(fetchBoxTypes.fulfilled, function (state, action) {
            state.loading.boxTypes = false;
            state.boxTypes = action.payload || [];
        })
            .addCase(fetchBoxTypes.rejected, function (state, action) {
            state.loading.boxTypes = false;
            state.errors.boxTypes = action.payload || 'Failed to fetch box types';
        });
        // Fetch weight UoMs
        builder
            .addCase(fetchWeightUoMs.pending, function (state) {
            state.loading.weightUoMs = true;
            state.errors.weightUoMs = null;
        })
            .addCase(fetchWeightUoMs.fulfilled, function (state, action) {
            state.loading.weightUoMs = false;
            state.weightUoMs = action.payload || [];
        })
            .addCase(fetchWeightUoMs.rejected, function (state, action) {
            state.loading.weightUoMs = false;
            state.errors.weightUoMs = action.payload || 'Failed to fetch weight UoMs';
        });
        // Fetch shipping methods
        builder
            .addCase(fetchShippingMethods.pending, function (state) {
            state.loading.shippingMethods = true;
            state.errors.shippingMethods = null;
        })
            .addCase(fetchShippingMethods.fulfilled, function (state, action) {
            state.loading.shippingMethods = false;
            state.shippingMethods = action.payload || [];
        })
            .addCase(fetchShippingMethods.rejected, function (state, action) {
            state.loading.shippingMethods = false;
            state.errors.shippingMethods =
                action.payload || 'Failed to fetch shipping methods';
        });
        // Fetch shipment list
        builder
            .addCase(fetchShipmentList.pending, function (state) {
            state.loading.shipmentList = true;
            state.errors.shipmentList = null;
        })
            .addCase(fetchShipmentList.fulfilled, function (state, action) {
            state.loading.shipmentList = false;
            if (action.payload) {
                state.shipmentList = action.payload.shipmentsList;
                state.shipmentListPageMaxIndex = action.payload.pageMaxIndex;
                state.shipmentListRecordCount = action.payload.recordCount;
                state.currentSubscreen = PackingSubscreen.ShipmentList;
            }
        })
            .addCase(fetchShipmentList.rejected, function (state, action) {
            state.loading.shipmentList = false;
            state.errors.shipmentList = action.payload || 'Failed to fetch shipment list';
        });
        // Fetch attachments
        builder
            .addCase(fetchAttachments.pending, function (state) {
            state.loading.attachments = true;
            state.errors.attachments = null;
        })
            .addCase(fetchAttachments.fulfilled, function (state, action) {
            state.loading.attachments = false;
            state.attachmentList = action.payload || [];
        })
            .addCase(fetchAttachments.rejected, function (state, action) {
            state.loading.attachments = false;
            state.errors.attachments = action.payload || 'Failed to fetch attachments';
        });
        // Fetch countries
        builder
            .addCase(fetchCountries.pending, function (state) {
            state.loading.countries = true;
            state.errors.countries = null;
        })
            .addCase(fetchCountries.fulfilled, function (state, action) {
            state.loading.countries = false;
            state.countriesList = action.payload || [];
        })
            .addCase(fetchCountries.rejected, function (state, action) {
            state.loading.countries = false;
            state.errors.countries = action.payload || 'Failed to fetch countries';
        });
        // Fetch states
        builder
            .addCase(fetchStates.pending, function (state) {
            state.loading.states = true;
            state.errors.states = null;
        })
            .addCase(fetchStates.fulfilled, function (state, action) {
            state.loading.states = false;
            state.statesList = action.payload || [];
        })
            .addCase(fetchStates.rejected, function (state, action) {
            state.loading.states = false;
            state.errors.states = action.payload || 'Failed to fetch states';
        });
        // Update address
        builder
            .addCase(updateAddress.pending, function (state) {
            state.loading.updateAddress = true;
            state.errors.updateAddress = null;
        })
            .addCase(updateAddress.fulfilled, function (state) {
            state.loading.updateAddress = false;
        })
            .addCase(updateAddress.rejected, function (state, action) {
            state.loading.updateAddress = false;
            state.errors.updateAddress = action.payload || 'Failed to update address';
        });
        // Get return label
        builder
            .addCase(getReturnLabel.pending, function (state) {
            state.loading.returnLabel = true;
            state.errors.returnLabel = null;
        })
            .addCase(getReturnLabel.fulfilled, function (state) {
            state.loading.returnLabel = false;
        })
            .addCase(getReturnLabel.rejected, function (state, action) {
            state.loading.returnLabel = false;
            state.errors.returnLabel = action.payload || 'Failed to get return label';
        });
        // Fetch user preferences
        builder
            .addCase(fetchUserPreferences.pending, function (state) {
            state.loading.userPreferences = true;
            state.errors.userPreferences = null;
        })
            .addCase(fetchUserPreferences.fulfilled, function (state, action) {
            state.loading.userPreferences = false;
            // Merge so hostname-scoped printer preferences fetched from the workstation
            // endpoint are not erased by the generic user-preferences response.
            state.userPreferences = __assign(__assign({}, state.userPreferences), (action.payload || {}));
        })
            .addCase(fetchUserPreferences.rejected, function (state, action) {
            state.loading.userPreferences = false;
            state.errors.userPreferences =
                action.payload || 'Failed to fetch user preferences';
        });
        // Set user preference
        builder.addCase(setUserPreference.fulfilled, function (state, action) {
            var _a;
            if (action.payload) {
                state.userPreferences = __assign(__assign({}, state.userPreferences), (_a = {}, _a[action.payload.key] = action.payload.value, _a));
            }
        });
        builder.addCase(fetchDefaultNetworkPrinterDetail.fulfilled, function (state, action) {
            state.userPreferences = __assign(__assign({}, state.userPreferences), (action.payload || {}));
        });
        builder.addCase(fetchPrinterList.fulfilled, function (state, action) {
            state.printerList = action.payload || [];
        });
        builder.addCase(setDefaultNetworkPrinterDetail.fulfilled, function (state, action) {
            state.userPreferences = __assign(__assign({}, state.userPreferences), (action.payload || {}));
        });
        // Fetch shipment item assets
        builder.addCase(fetchShipmentItemAssets.fulfilled, function (state, action) {
            state.itemAssets = action.payload || [];
        });
        // Fetch refund label
        builder.addCase(fetchRefundLabel.fulfilled, function (state) {
            // Handled by notification in thunk
        });
        // Fetch facilities
        builder.addCase(fetchFacilities.fulfilled, function (state, action) {
            state.facilitiesData = action.payload || [];
        });
        // Fetch enumeration (handles multiple enum types)
        builder.addCase(fetchEnumeration.fulfilled, function (state, action) {
            var enumType = action.meta.arg.enumType;
            if (enumType === 'ShipmentType') {
                state.shipmentTypeList = action.payload || [];
            }
            else if (enumType === 'ShipmentMethod') {
                state.shipmentTypeMethodList = action.payload || [];
            }
            else if (enumType === 'FacilityLocationType') {
                state.shipmentLocationTypeList = action.payload || [];
            }
            else if (enumType === 'ContainerType') {
                state.containerTypeList = action.payload || [];
            }
        });
        // Fetch status list
        builder.addCase(fetchStatusList.fulfilled, function (state, action) {
            state.shipmentStatusList = action.payload || [];
        });
        // Initialize packing (loading state)
        builder
            .addCase(initializePacking.pending, function (state) {
            state.loading.shipment = true;
        })
            .addCase(initializePacking.fulfilled, function (state) {
            state.loading.shipment = false;
        })
            .addCase(initializePacking.rejected, function (state) {
            state.loading.shipment = false;
        });
        // Container scan
        builder
            .addCase(containerScan.pending, function (state, action) {
            var _a;
            if ((_a = action.meta.arg) === null || _a === void 0 ? void 0 : _a.suppressRefresh)
                return;
            state.loading.containerScan = true;
            state.errors.containerScan = null;
        })
            .addCase(containerScan.fulfilled, function (state, action) {
            var _a;
            if ((_a = action.meta.arg) === null || _a === void 0 ? void 0 : _a.suppressRefresh)
                return;
            state.loading.containerScan = false;
        })
            .addCase(containerScan.rejected, function (state, action) {
            var _a;
            if ((_a = action.meta.arg) === null || _a === void 0 ? void 0 : _a.suppressRefresh)
                return;
            state.loading.containerScan = false;
            state.errors.containerScan = action.payload || 'Container scan failed';
        });
        // Container bulk create
        builder
            .addCase(containerBulkCreate.pending, function (state) {
            state.loading.containerScan = true;
            state.errors.containerScan = null;
        })
            .addCase(containerBulkCreate.fulfilled, function (state) {
            state.loading.containerScan = false;
        })
            .addCase(containerBulkCreate.rejected, function (state, action) {
            state.loading.containerScan = false;
            state.errors.containerScan = action.payload || 'Bulk container creation failed';
        });
        // Container create (empty container)
        builder
            .addCase(containerCreate.pending, function (state) {
            state.loading.containerCreate = true;
            state.errors.containerCreate = null;
        })
            .addCase(containerCreate.fulfilled, function (state) {
            state.loading.containerCreate = false;
        })
            .addCase(containerCreate.rejected, function (state, action) {
            state.loading.containerCreate = false;
            state.errors.containerCreate = action.payload || 'Failed to create container';
        });
        // Container close
        builder
            .addCase(containerClose.pending, function (state) {
            state.loading.containerClose = true;
            state.errors.containerClose = null;
        })
            .addCase(containerClose.fulfilled, function (state) {
            state.loading.containerClose = false;
        })
            .addCase(containerClose.rejected, function (state, action) {
            state.loading.containerClose = false;
            state.errors.containerClose = action.payload || 'Failed to close container';
        });
        // Container reopen
        builder
            .addCase(containerReopen.pending, function (state) {
            state.loading.containerClose = true;
            state.errors.containerClose = null;
        })
            .addCase(containerReopen.fulfilled, function (state) {
            state.loading.containerClose = false;
        })
            .addCase(containerReopen.rejected, function (state, action) {
            state.loading.containerClose = false;
            state.errors.containerClose = action.payload || 'Failed to reopen container';
        });
        // Container delete
        builder
            .addCase(containerDelete.pending, function (state) {
            state.loading.containerDelete = true;
            state.errors.containerDelete = null;
        })
            .addCase(containerDelete.fulfilled, function (state) {
            state.loading.containerDelete = false;
        })
            .addCase(containerDelete.rejected, function (state, action) {
            state.loading.containerDelete = false;
            state.errors.containerDelete = action.payload || 'Failed to delete container';
        });
        // Container reset
        builder
            .addCase(containerReset.pending, function (state) {
            state.loading.containerReset = true;
            state.errors.containerReset = null;
        })
            .addCase(containerReset.fulfilled, function (state) {
            state.loading.containerReset = false;
        })
            .addCase(containerReset.rejected, function (state, action) {
            state.loading.containerReset = false;
            state.errors.containerReset = action.payload || 'Failed to reset container';
        });
        // Container reset & return (adopted containers only)
        builder
            .addCase(containerResetAndReturn.pending, function (state) {
            state.loading.containerResetAndReturn = true;
            state.errors.containerResetAndReturn = null;
        })
            .addCase(containerResetAndReturn.fulfilled, function (state) {
            state.loading.containerResetAndReturn = false;
        })
            .addCase(containerResetAndReturn.rejected, function (state, action) {
            state.loading.containerResetAndReturn = false;
            state.errors.containerResetAndReturn =
                action.payload || 'Failed to return container to inventory';
        });
    }
});
export var resetPackingState = (_a = packingSlice.actions, _a.resetPackingState), setCurrentSubscreen = _a.setCurrentSubscreen, setContainerId = _a.setContainerId, setBulkPackMode = _a.setBulkPackMode, setScanThroughPack = _a.setScanThroughPack, setScanIdCacheEntry = _a.setScanIdCacheEntry, setScanQueueStatus = _a.setScanQueueStatus, clearShipmentData = _a.clearShipmentData, clearErrors = _a.clearErrors, clearError = _a.clearError, setShipmentStatusList = _a.setShipmentStatusList, setShipmentTypeList = _a.setShipmentTypeList, setShipmentTypeMethodList = _a.setShipmentTypeMethodList, setShipmentLocationTypeList = _a.setShipmentLocationTypeList, setFacilitiesData = _a.setFacilitiesData, setQzDeviceContext = _a.setQzDeviceContext, setQzConnectionActive = _a.setQzConnectionActive;
export default packingSlice.reducer;
